import _ from "lodash";
/** -- external -- */
import React, { useRef } from "react";
import { connect } from "react-redux";

/**-- internal -- */
import {
  UserCurriculumProgramsContext,
  getCurriculumProgramFreeStatus,
} from "Platform/modules/PlatformModule";
import { useOnClickOutside, useI18n } from "Hooks";

/**-- relative -- */
import { CourseFeed, ProjectsFeed, HomepageFeatures } from "./components";
import classes from "./JumpToFeature.scss";

import {
  getVisibleJumpToFeatures,
  getIsGlobalSearchJumpToRevampEnabled,
  JUMP_TO_COLUMNS,
} from "GlobalHeader/modules/Utils";
import { GLOBAL_HEADER_SHORTCUTS_MAPPING } from "Platform/modules/PlatformUtils";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getCurrentCurriculumContextForJumpTo } from "modules/CommonSelectors";
import { EmptyState } from "@toddle-edu/ds-web";
import { NoAccessIllustration } from "@toddle-edu/ds-theme";
import { getIsLearningCoursesEnabled } from "Teacher/routes/LearningCourses/modules/Utils";

const JumpToFeature = ({
  onSelectFeatureCallback,
  projectGroups,
  visibleJumpToFeatures,
  updateAvailableShortcutsMapping,
  closeJumpToFeature,
  isCurriculumProgramFree,
  currentCurriculumProgramId,
  homeroomStudentCount,
  isLearningCoursesEnabled,
  totalNewSnpCycles,
  isGlobalSearchJumpToRevampEnabled,
}) => {
  const containerRef = useRef();
  const { t } = useI18n(["common"]);

  const testId = "jumpTo";

  const handleOnClickOutside = () => {
    if (!isGlobalSearchJumpToRevampEnabled) {
      closeJumpToFeature();
    }
  };

  useOnClickOutside(containerRef, handleOnClickOutside);

  const mapSectionsWithShortcuts = sections => {
    return _.pick(GLOBAL_HEADER_SHORTCUTS_MAPPING, sections);
  };

  const renderFeature = (feature, index) => {
    switch (feature.key) {
      case "LEARNING_COURSE_FEED": {
        const updateAvailableCourses = courses => {
          const courseShortcutsMapping = _.pickBy(
            GLOBAL_HEADER_SHORTCUTS_MAPPING,
            (value, key) => _.includes(key, "COURSE")
          );

          const keys = _.keys(courseShortcutsMapping);

          /**
           * Here, shortcuts for course have structure like: COURSE_1, COURSE_2 etc.
           * So, we are using suffix to associate the course with the shortcut.
           */
          const updatedCourseShortcutsMapping = _.reduce(
            keys,
            (result, key) => {
              const courseIndex = _.split(key, "_")?.[2];

              if (+courseIndex <= courses.length) {
                const learningCourseId = _.get(
                  courses,
                  [+courseIndex - 1, "id"],
                  null
                );
                result[key] = {
                  learningCourseId,
                  ...courseShortcutsMapping[key],
                };
              }

              return result;
            },
            {}
          );

          updateAvailableShortcutsMapping({
            key: "LEARNING_COURSE_FEED",
            value: updatedCourseShortcutsMapping,
          });
        };
        return (
          <CourseFeed
            onNavigationDone={onSelectFeatureCallback}
            isCurriculumProgramFree={isCurriculumProgramFree}
            isLearningCoursesEnabled={isLearningCoursesEnabled}
            columnIndex={index}
            feedType={JUMP_TO_COLUMNS.LEARNING_COURSE_FEED}
            currentCurriculumProgramId={currentCurriculumProgramId}
            testId={testId}
            updateAvailableCourses={updateAvailableCourses}
          />
        );
      }
      case "COURSE_FEED": {
        const updateAvailableCourses = courses => {
          const courseShortcutsMapping = _.pickBy(
            GLOBAL_HEADER_SHORTCUTS_MAPPING,
            (value, key) => _.includes(key, "COURSE")
          );

          const keys = _.keys(courseShortcutsMapping);

          /**
           * Here, shortcuts for course have structure like: COURSE_1, COURSE_2 etc.
           * So, we are using suffix to associate the course with the shortcut.
           */
          const updatedCourseShortcutsMapping = _.reduce(
            keys,
            (result, key) => {
              const [prefixKey, courseIndex] = _.split(key, "_");

              if (+courseIndex <= courses.length) {
                const course = _.get(courses, [+courseIndex - 1], {});
                const courseId = _.get(course, "id", null);
                const learningCourseId = _.get(
                  course,
                  "learningCourse[0].id",
                  null
                );

                result[key] = {
                  courseId,
                  learningCourseId,
                  ...courseShortcutsMapping[key],
                };
              }

              return result;
            },
            {}
          );

          updateAvailableShortcutsMapping({
            key: "COURSE_FEED",
            value: updatedCourseShortcutsMapping,
          });
        };

        return (
          <CourseFeed
            onNavigationDone={onSelectFeatureCallback}
            testId={testId}
            updateAvailableCourses={updateAvailableCourses}
            isCurriculumProgramFree={isCurriculumProgramFree}
            isLearningCoursesEnabled={isLearningCoursesEnabled}
            currentCurriculumProgramId={currentCurriculumProgramId}
            feedType={JUMP_TO_COLUMNS.COURSE_FEED}
            columnIndex={index}
          />
        );
      }
      case "PROJECTS_FEED":
        return (
          <ProjectsFeed
            projectGroups={projectGroups}
            onNavigationDone={onSelectFeatureCallback}
            updateAvailableProjects={projects => {
              const projectsShortcutMapping = _.pickBy(
                GLOBAL_HEADER_SHORTCUTS_MAPPING,
                (value, key) => _.includes(key, "PROJECT")
              );

              const keys = _.keys(projectsShortcutMapping);

              const updatedProjectsShortcutsMapping = _.reduce(
                keys,
                (result, key) => {
                  const [prefixKey, projectIndex] = _.split(key, "_");

                  if (+projectIndex <= projects.length) {
                    result[key] = {
                      projectGroupType: projects[+projectIndex - 1],
                      ...projectsShortcutMapping[key],
                    };
                  }

                  return result;
                },
                {}
              );

              updateAvailableShortcutsMapping({
                key: "PROJECTS_FEED",
                value: updatedProjectsShortcutsMapping,
              });
            }}
            columnIndex={index}
            testId={testId}
          />
        );
      case "HOMEPAGE_FEATURES":
        return (
          <UserCurriculumProgramsContext.Consumer>
            {userCurriculumPrograms => (
              <HomepageFeatures
                onNavigationDone={onSelectFeatureCallback}
                userCurriculumPrograms={userCurriculumPrograms}
                updateAvailableHomepageFeatures={features => {
                  const shortcuts = mapSectionsWithShortcuts(features);
                  updateAvailableShortcutsMapping({
                    key: "HOMEPAGE_FEATURES",
                    value: shortcuts,
                  });
                }}
                homeroomStudentCount={homeroomStudentCount}
                totalNewSnpCycles={totalNewSnpCycles}
                columnIndex={index}
                testId={testId}
                currentCurriculumProgramId={currentCurriculumProgramId}
              />
            )}
          </UserCurriculumProgramsContext.Consumer>
        );
      default:
        return null;
    }
  };

  const containerClass = isGlobalSearchJumpToRevampEnabled
    ? classes.containerV2
    : classes.container;

  const columnsClass = isGlobalSearchJumpToRevampEnabled
    ? classes.columnsV2
    : classes.columns;

  return (
    <div className={containerClass} ref={containerRef}>
      {_.map(visibleJumpToFeatures, (feature, index) => (
        <div key={feature.key} className={columnsClass}>
          {renderFeature(feature, index)}
        </div>
      ))}
      {_.isEmpty(visibleJumpToFeatures) && (
        <div className={classes.emptyStateContainer}>
          <EmptyState
            dsVersion={"2.0"}
            illustration={NoAccessIllustration}
            size="large"
            title={t("common:no_access_to_program_modules")}
            subtitle={t("common:contact_school_admin")}
          />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const {
    userCoursesCount = 0,
    userLearningCoursesCount = 0,
    projectGroups,
  } = ownProps;

  const currentCurriculumContext = getCurrentCurriculumContextForJumpTo(state);

  const tabConfigurations = {
    admin: currentCurriculumContext,
    teacher: currentCurriculumContext,
  };

  const isCurriculumProgramFree = getCurriculumProgramFreeStatus({
    state,
    tabConfigurations,
  });

  const {
    id: currentCurriculumProgramId,
    type: currentCurriculumProgramType,
  } = currentCurriculumContext;

  const userInfo = getUserInfo();
  const { org_id: organizationId } = userInfo;

  const visibleJumpToFeatures = getVisibleJumpToFeatures({
    userCoursesCount,
    userLearningCoursesCount,
    projectGroups,
    isCurriculumProgramFree,
    currentCurriculumProgramId,
    currentCurriculumProgramType,
    organizationId,
  });

  const isGlobalSearchJumpToRevampEnabled = getIsGlobalSearchJumpToRevampEnabled();

  const isLearningCoursesEnabled = getIsLearningCoursesEnabled({
    curriculumProgramId: currentCurriculumProgramId,
  });

  return {
    projectGroups,
    visibleJumpToFeatures,
    isCurriculumProgramFree,
    isLearningCoursesEnabled,
    currentCurriculumProgramId,
    isGlobalSearchJumpToRevampEnabled,
  };
};

const JumpToFeatureWrapper = connect(mapStateToProps)(JumpToFeature);

export default JumpToFeatureWrapper;
